import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
  Badge,
  Input,
  FormControl,
  FormLabel,
  Tooltip,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link,
} from "@chakra-ui/react";
import axios from "axios";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../firebase";
import { apiUrl } from "../config";
import Navbar from "../components/Navbar";
import DigitalCardsAdmin from "../components/DigitalCardsAdmin";
import SurveyResultsAdmin from "../components/SurveyResultsAdmin";
import TemplatesAdmin from "../components/TemplatesAdmin";
import {
  FaChevronRight,
  FaChevronDown,
  FaCode,
  FaEnvelope,
  FaExternalLinkAlt,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

const adminAuthHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("idToken")}`,
});

// Org/campaign names are stored as { en, fr } localization objects in some
// docs and as plain strings in others. Normalize to a string for display.
const displayName = (name) => {
  if (!name) return "—";
  if (typeof name === "string") return name;
  if (typeof name === "object") return name.en || name.fr || "—";
  return String(name);
};

const formatShortDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // Format in UTC: "YYYY-MM-DD" strings parse as UTC midnight, so without
  // this a viewer west of UTC sees the day before what was stored.
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatRedemptionRange = (startIso, endIso) => {
  const start = formatShortDate(startIso);
  const end = formatShortDate(endIso);
  if (!start && !end) return null;
  return `${start || "?"} - ${end || "?"}`;
};

const IdCopyIcon = ({ id, label = "ID" }) => {
  const toast = useToast();
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      toast({
        title: `${label} copied`,
        description: id,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  return (
    <Tooltip label={id} fontFamily="mono" fontSize="xs" hasArrow>
      <IconButton
        aria-label={`Copy ${label}`}
        icon={<FaCode />}
        size="xs"
        variant="ghost"
        color="gray.500"
        onClick={handleCopy}
      />
    </Tooltip>
  );
};

const EmailIcon = ({ email }) => {
  if (!email) return <Text as="span">—</Text>;
  return (
    <Tooltip label={email} fontSize="xs" hasArrow>
      <IconButton
        as="a"
        href={`mailto:${email}`}
        aria-label={`Email ${email}`}
        icon={<FaEnvelope />}
        size="xs"
        variant="ghost"
        color="gray.500"
      />
    </Tooltip>
  );
};

const PlatformDashboard = () => {
  const toast = useToast();
  const [organizations, setOrganizations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonationTarget, setImpersonationTarget] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const [expandedOrgIds, setExpandedOrgIds] = useState(() => new Set());
  const [expandedCampaignIds, setExpandedCampaignIds] = useState(
    () => new Set(),
  );
  const [campaignAgents, setCampaignAgents] = useState({});
  const [campaignAgentsLoading, setCampaignAgentsLoading] = useState({});
  const [campaignBusinesses, setCampaignBusinesses] = useState({});
  const [campaignBusinessesLoading, setCampaignBusinessesLoading] = useState(
    {},
  );

  const [usersSearch, setUsersSearch] = useState("");

  const filteredUsers = React.useMemo(() => {
    const term = usersSearch.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => {
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ");
      return [fullName, u.email, u.uid]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [users, usersSearch]);

  const [customersCampaignId, setCustomersCampaignId] = useState("");
  const [customersCampaignSearch, setCustomersCampaignSearch] = useState("");
  const [customersList, setCustomersList] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  const campaignSearchMatches = React.useMemo(() => {
    const term = customersCampaignSearch.trim().toLowerCase();
    if (!term) return [];
    return campaigns
      .filter((c) => {
        const name = displayName(c.name).toLowerCase();
        const orgName =
          displayName(
            (organizations.find((o) => o.id === c.organizationId) || {}).name,
          ).toLowerCase();
        return name.includes(term) || orgName.includes(term);
      })
      .slice(0, 8);
  }, [campaigns, organizations, customersCampaignSearch]);

  const selectedCustomersCampaign = React.useMemo(
    () => campaigns.find((c) => c.id === customersCampaignId) || null,
    [campaigns, customersCampaignId],
  );

  const fetchCampaignCustomers = async (campaignId) => {
    if (!campaignId) return;
    setCustomersLoading(true);
    try {
      const res = await axios.get(
        `${apiUrl}/admin/campaigns/${campaignId}/customers`,
        { headers: adminAuthHeader() },
      );
      setCustomersList(res.data.customers || []);
    } catch (err) {
      console.error("Failed to load campaign customers", err);
      toast({
        title: "Error",
        description: "Failed to load campaign customers",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setCustomersList([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  const selectCustomersCampaign = (campaign) => {
    setCustomersCampaignId(campaign.id);
    setCustomersCampaignSearch("");
    fetchCampaignCustomers(campaign.id);
  };

  const clearCustomersCampaign = () => {
    setCustomersCampaignId("");
    setCustomersList([]);
  };

  const exportCustomersCsv = () => {
    if (!customersList.length || !selectedCustomersCampaign) return;
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "Name",
      "Email",
      "Zip/Postal",
      "Redemptions",
      "Created",
      "Customer ID",
    ];
    const rows = customersList.map((c) => [
      c.name || "",
      c.email || "",
      c.postalCode || "",
      c.redemptionCount,
      formatShortDate(c.createdAt) || "",
      c.id,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(escape).join(","))
      .join("\r\n");
    const blob = new Blob([`﻿${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const slug = displayName(selectedCustomersCampaign.name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "campaign";
    const today = new Date().toISOString().slice(0, 10);
    const link = document.createElement("a");
    link.href = url;
    link.download = `customers-${slug}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const campaignsByOrgId = React.useMemo(() => {
    const map = new Map();
    campaigns.forEach((c) => {
      const list = map.get(c.organizationId) || [];
      list.push(c);
      map.set(c.organizationId, list);
    });
    return map;
  }, [campaigns]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const headers = adminAuthHeader();
        const [orgsRes, campsRes, usersRes] = await Promise.all([
          axios.get(`${apiUrl}/admin/organizations`, { headers }),
          axios.get(`${apiUrl}/admin/campaigns`, { headers }),
          axios.get(`${apiUrl}/admin/users`, { headers }),
        ]);
        setOrganizations(orgsRes.data.organizations || []);
        setCampaigns(campsRes.data.campaigns || []);
        setUsers(usersRes.data.users || []);
      } catch (err) {
        console.error("Failed to load platform dashboard data", err);
        toast({
          title: "Error",
          description: "Failed to load platform dashboard data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const openImpersonateModal = (target) => {
    setImpersonationTarget(target);
    onOpen();
  };

  const toggleOrgExpand = (orgId) => {
    setExpandedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  const fetchCampaignAgents = async (campaignId) => {
    if (campaignAgents[campaignId] || campaignAgentsLoading[campaignId]) return;
    setCampaignAgentsLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const res = await axios.get(
        `${apiUrl}/admin/campaigns/${campaignId}/agents`,
        { headers: adminAuthHeader() },
      );
      setCampaignAgents((prev) => ({
        ...prev,
        [campaignId]: res.data.agents || [],
      }));
    } catch (err) {
      console.error("Failed to load campaign agents", err);
      toast({
        title: "Error",
        description: "Failed to load campaign agents",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCampaignAgentsLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  const fetchCampaignBusinesses = async (campaignId) => {
    if (
      campaignBusinesses[campaignId] ||
      campaignBusinessesLoading[campaignId]
    )
      return;
    setCampaignBusinessesLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const res = await axios.get(
        `${apiUrl}/admin/campaigns/${campaignId}/businesses`,
        { headers: adminAuthHeader() },
      );
      setCampaignBusinesses((prev) => ({
        ...prev,
        [campaignId]: res.data.businesses || [],
      }));
    } catch (err) {
      console.error("Failed to load campaign businesses", err);
      toast({
        title: "Error",
        description: "Failed to load campaign businesses",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCampaignBusinessesLoading((prev) => ({
        ...prev,
        [campaignId]: false,
      }));
    }
  };

  const toggleCampaignExpand = (campaignId) => {
    setExpandedCampaignIds((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
        fetchCampaignAgents(campaignId);
        fetchCampaignBusinesses(campaignId);
      }
      return next;
    });
  };

  const openDeleteModal = (target, type) => {
    setDeleteTarget(target);
    setDeleteType(type);
    setDeleteConfirmText("");
    onDeleteOpen();
  };

  const [approvingOrgId, setApprovingOrgId] = useState(null);
  const approveOrganization = async (org) => {
    try {
      setApprovingOrgId(org.id);
      await axios.patch(
        `${apiUrl}/admin/organizations/${org.id}/approve`,
        {},
        { headers: adminAuthHeader() },
      );
      setOrganizations((prev) =>
        prev.map((o) => (o.id === org.id ? { ...o, approved: true } : o)),
      );
      toast({
        title: "Organization approved",
        description: `“${displayName(org.name)}” is now approved.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Approve organization failed", err);
      toast({
        title: "Approve failed",
        description:
          err?.response?.data?.error || err.message || "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setApprovingOrgId(null);
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteType(null);
    setDeleteConfirmText("");
    onDeleteClose();
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deleteType || deleteConfirmText !== "delete") return;
    const isOrg = deleteType === "organization";
    const path = isOrg
      ? `${apiUrl}/admin/organizations/${deleteTarget.id}`
      : `${apiUrl}/admin/campaigns/${deleteTarget.id}`;
    try {
      setDeleting(true);
      const res = await axios.delete(path, { headers: adminAuthHeader() });
      if (isOrg) {
        setOrganizations((prev) =>
          prev.filter((o) => o.id !== deleteTarget.id),
        );
      } else {
        setCampaigns((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      }
      toast({
        title: `${isOrg ? "Organization" : "Campaign"} deleted`,
        description: `Removed “${displayName(deleteTarget.name)}” (${
          res.data.agentsDeleted ?? 0
        } agent doc(s) cleaned up).`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setDeleteTarget(null);
      setDeleteType(null);
      setDeleteConfirmText("");
      onDeleteClose();
    } catch (err) {
      console.error(`Delete ${deleteType} failed`, err);
      toast({
        title: "Delete failed",
        description:
          err?.response?.data?.error || err.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const confirmImpersonate = async () => {
    if (!impersonationTarget) return;
    try {
      setImpersonating(true);
      const res = await axios.post(
        `${apiUrl}/admin/impersonate`,
        { uid: impersonationTarget.uid },
        { headers: adminAuthHeader() },
      );
      const { customToken } = res.data;
      sessionStorage.setItem("signInMethod", "impersonation");
      await signInWithCustomToken(auth, customToken);
      // AuthContext's onAuthStateChanged listener picks up the new user and
      // routes via its navigation effect.
      onClose();
    } catch (err) {
      console.error("Impersonation failed", err);
      toast({
        title: "Impersonation failed",
        description:
          err?.response?.data?.error || err.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setImpersonating(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box
        minH="calc(100vh - 60px)"
        bg="primaryBackground"
        pt={{ base: "40px", md: "30px" }}
        px={{ base: 4, sm: 6, md: 10 }}
      >
        <VStack
          spacing={6}
          maxWidth="1200px"
          margin="0 auto"
          py={{ base: 6, md: 8 }}
          width="100%"
          align="stretch"
        >
          <Heading as="h1" size="lg" color="heading">
            Platform Dashboard
          </Heading>

          {loading ? (
            <HStack justify="center" py={10}>
              <Spinner />
              <Text>Loading…</Text>
            </HStack>
          ) : (
            <Box bg="white" borderRadius="xl" boxShadow="md" p={{ base: 4, md: 6 }}>
              <Tabs variant="brand">
                <TabList>
                  <Tab>Organizations ({organizations.length})</Tab>
                  <Tab>Users ({users.length})</Tab>
                  <Tab>Customers</Tab>
                  <Tab>Survey Results</Tab>
                  <Tab>Digital Cards</Tab>
                  <Tab>Templates</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={0} textAlign="left">
                      {organizations.length === 0 && (
                        <Text color="gray.500" py={4}>
                          No organizations.
                        </Text>
                      )}
                      {organizations.map((org) => {
                        const orgExpanded = expandedOrgIds.has(org.id);
                        const orgCampaigns =
                          campaignsByOrgId.get(org.id) || [];
                        return (
                          <Box
                            key={org.id}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                          >
                            <HStack
                              spacing={2}
                              py={2}
                              px={1}
                              cursor="pointer"
                              _hover={{ bg: "gray.50" }}
                              onClick={() => toggleOrgExpand(org.id)}
                            >
                              <Box w={3} textAlign="center" flexShrink={0}>
                                {orgExpanded ? (
                                  <FaChevronDown size={10} />
                                ) : (
                                  <FaChevronRight size={10} />
                                )}
                              </Box>
                              <HStack spacing={2} flex="1" minW={0}>
                                <Text fontWeight="semibold">
                                  {displayName(org.name)}
                                </Text>
                                <IdCopyIcon
                                  id={org.id}
                                  label="Organization ID"
                                />
                                {org.approved ? (
                                  <Badge colorScheme="green">Approved</Badge>
                                ) : (
                                  <>
                                    <Badge colorScheme="orange">Pending</Badge>
                                    <Button
                                      size="xs"
                                      colorScheme="green"
                                      variant="outline"
                                      isLoading={approvingOrgId === org.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        approveOrganization(org);
                                      }}
                                    >
                                      Approve Organization
                                    </Button>
                                  </>
                                )}
                              </HStack>
                              <Badge variant="subtle">
                                {orgCampaigns.length} campaign
                                {orgCampaigns.length === 1 ? "" : "s"}
                              </Badge>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(org, "organization");
                                }}
                              >
                                Delete
                              </Button>
                            </HStack>

                            {orgExpanded && (
                              <Box pl={4} pb={2}>
                                {orgCampaigns.length === 0 ? (
                                  <Text
                                    color="gray.500"
                                    fontSize="sm"
                                    py={2}
                                  >
                                    No campaigns.
                                  </Text>
                                ) : (
                                  orgCampaigns.map((camp) => {
                                    const campExpanded =
                                      expandedCampaignIds.has(camp.id);
                                    const agents =
                                      campaignAgents[camp.id] || [];
                                    const agentsLoading =
                                      campaignAgentsLoading[camp.id];
                                    const sortedAgents = [...agents].sort(
                                      (a, b) =>
                                        Number(b.isAdmin) - Number(a.isAdmin),
                                    );
                                    const businesses =
                                      campaignBusinesses[camp.id] || [];
                                    const businessesLoading =
                                      campaignBusinessesLoading[camp.id];
                                    return (
                                      <Box key={camp.id}>
                                        <HStack
                                          spacing={2}
                                          py={2}
                                          px={1}
                                          cursor="pointer"
                                          _hover={{ bg: "gray.50" }}
                                          onClick={() =>
                                            toggleCampaignExpand(camp.id)
                                          }
                                        >
                                          <Box
                                            w={3}
                                            textAlign="center"
                                            flexShrink={0}
                                          >
                                            {campExpanded ? (
                                              <FaChevronDown size={10} />
                                            ) : (
                                              <FaChevronRight size={10} />
                                            )}
                                          </Box>
                                          <HStack
                                            spacing={2}
                                            flex="1"
                                            minW={0}
                                          >
                                            <Text>
                                              {displayName(camp.name)}
                                            </Text>
                                            <IdCopyIcon
                                              id={camp.id}
                                              label="Campaign ID"
                                            />
                                            {camp.active ? (
                                              <Badge colorScheme="green">
                                                Active
                                              </Badge>
                                            ) : (
                                              <Badge colorScheme="gray">
                                                Inactive
                                              </Badge>
                                            )}
                                          </HStack>
                                          {(() => {
                                            const range = formatRedemptionRange(
                                              camp.redemptionStartDate,
                                              camp.redemptionEndDate,
                                            );
                                            return range ? (
                                              <Text
                                                fontSize="sm"
                                                color="gray.600"
                                              >
                                                {range}
                                              </Text>
                                            ) : null;
                                          })()}
                                          <Text
                                            fontSize="sm"
                                            color="green.700"
                                            fontWeight="medium"
                                          >
                                            $
                                            {(camp.totalRaised || 0).toFixed(
                                              2,
                                            )}
                                          </Text>
                                          <Button
                                            size="xs"
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openDeleteModal(camp, "campaign");
                                            }}
                                          >
                                            Delete
                                          </Button>
                                        </HStack>

                                        {campExpanded && (
                                          <Box pl={4} pb={2}>
                                            {agentsLoading ? (
                                              <HStack py={2}>
                                                <Spinner size="xs" />
                                                <Text
                                                  fontSize="sm"
                                                  color="gray.500"
                                                >
                                                  Loading users…
                                                </Text>
                                              </HStack>
                                            ) : sortedAgents.length === 0 ? (
                                              <Text
                                                fontSize="sm"
                                                color="gray.500"
                                                py={2}
                                              >
                                                No campaign users.
                                              </Text>
                                            ) : (
                                              sortedAgents.map((agentUser) => {
                                                const fullName =
                                                  [
                                                    agentUser.firstName,
                                                    agentUser.lastName,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" ") || "—";
                                                return (
                                                  <HStack
                                                    key={agentUser.uid}
                                                    spacing={2}
                                                    py={1}
                                                    px={1}
                                                  >
                                                    <Box w={3} flexShrink={0} />
                                                    <Text
                                                      fontSize="sm"
                                                      flex="1"
                                                    >
                                                      {fullName}
                                                      <Badge
                                                        ml={2}
                                                        colorScheme={
                                                          agentUser.isAdmin
                                                            ? "blue"
                                                            : "gray"
                                                        }
                                                        fontSize="0.6em"
                                                      >
                                                        {agentUser.isAdmin
                                                          ? "Admin"
                                                          : "Team"}
                                                      </Badge>
                                                      {agentUser.email && (
                                                        <Text
                                                          as="span"
                                                          color="gray.500"
                                                          ml={2}
                                                        >
                                                          {agentUser.email}
                                                        </Text>
                                                      )}
                                                    </Text>
                                                    <Button
                                                      size="xs"
                                                      variant="outline"
                                                      onClick={() =>
                                                        openImpersonateModal(
                                                          agentUser,
                                                        )
                                                      }
                                                    >
                                                      Impersonate
                                                    </Button>
                                                  </HStack>
                                                );
                                              })
                                            )}
                                            {businessesLoading ? (
                                              <HStack py={2}>
                                                <Spinner size="xs" />
                                                <Text
                                                  fontSize="sm"
                                                  color="gray.500"
                                                >
                                                  Loading businesses…
                                                </Text>
                                              </HStack>
                                            ) : businesses.length === 0 ? (
                                              <Text
                                                fontSize="sm"
                                                color="gray.500"
                                                py={2}
                                              >
                                                No campaign businesses.
                                              </Text>
                                            ) : (
                                              businesses.map((biz) => (
                                                <HStack
                                                  key={biz.id}
                                                  spacing={2}
                                                  py={1}
                                                  px={1}
                                                >
                                                  <Box w={3} flexShrink={0} />
                                                  <Text
                                                    fontSize="sm"
                                                    flex="1"
                                                  >
                                                    {displayName(biz.name)}
                                                    <Badge
                                                      ml={2}
                                                      colorScheme="purple"
                                                      fontSize="0.6em"
                                                    >
                                                      Business
                                                    </Badge>
                                                    {biz.approved ? (
                                                      <Badge
                                                        ml={2}
                                                        colorScheme="green"
                                                        fontSize="0.6em"
                                                      >
                                                        Approved
                                                      </Badge>
                                                    ) : (
                                                      <Badge
                                                        ml={2}
                                                        colorScheme="orange"
                                                        fontSize="0.6em"
                                                      >
                                                        Pending
                                                      </Badge>
                                                    )}
                                                    {biz.contactName && (
                                                      <Text
                                                        as="span"
                                                        color="gray.500"
                                                        ml={2}
                                                      >
                                                        {biz.contactName}
                                                      </Text>
                                                    )}
                                                    {biz.email && (
                                                      <Text
                                                        as="span"
                                                        color="gray.500"
                                                        ml={2}
                                                      >
                                                        {biz.email}
                                                      </Text>
                                                    )}
                                                  </Text>
                                                  {biz.ownerUid ? (
                                                    <Button
                                                      size="xs"
                                                      variant="outline"
                                                      onClick={() =>
                                                        openImpersonateModal({
                                                          uid: biz.ownerUid,
                                                          email: biz.email,
                                                          firstName:
                                                            biz.contactName,
                                                          lastName: null,
                                                        })
                                                      }
                                                    >
                                                      Impersonate
                                                    </Button>
                                                  ) : (
                                                    <Tooltip
                                                      label="No owner account linked to this business"
                                                      hasArrow
                                                    >
                                                      <Box>
                                                        <Button
                                                          size="xs"
                                                          variant="outline"
                                                          isDisabled
                                                        >
                                                          Impersonate
                                                        </Button>
                                                      </Box>
                                                    </Tooltip>
                                                  )}
                                                </HStack>
                                              ))
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={3}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none" color="gray.400">
                          <FaSearch />
                        </InputLeftElement>
                        <Input
                          placeholder="Filter by name, email, or UID…"
                          value={usersSearch}
                          onChange={(e) => setUsersSearch(e.target.value)}
                        />
                        {usersSearch && (
                          <InputRightElement>
                            <IconButton
                              icon={<FaTimes />}
                              size="xs"
                              variant="ghost"
                              aria-label="Clear search"
                              onClick={() => setUsersSearch("")}
                            />
                          </InputRightElement>
                        )}
                      </InputGroup>
                      <Box overflowX="auto">
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Name</Th>
                              <Th>Email</Th>
                              <Th>UID</Th>
                              <Th>Role</Th>
                              <Th />
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filteredUsers.map((u) => {
                            const fullName =
                              [u.firstName, u.lastName]
                                .filter(Boolean)
                                .join(" ") || "—";
                            return (
                              <Tr key={u.uid}>
                                <Td>{fullName}</Td>
                                <Td>{u.email || "—"}</Td>
                                <Td fontFamily="mono" fontSize="xs">
                                  {u.uid}
                                </Td>
                                <Td>
                                  {u.isPlatformAdmin ? (
                                    <Badge colorScheme="purple">
                                      Platform Admin
                                    </Badge>
                                  ) : (
                                    <Badge>User</Badge>
                                  )}
                                </Td>
                                <Td>
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={() => openImpersonateModal(u)}
                                  >
                                    Impersonate
                                  </Button>
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={4}>
                      {!selectedCustomersCampaign ? (
                        <Box position="relative">
                          <Text fontSize="sm" color="gray.600" mb={2}>
                            Select a campaign to view its customers.
                          </Text>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.400"
                            >
                              <FaSearch />
                            </InputLeftElement>
                            <Input
                              placeholder="Search by campaign or organization name…"
                              value={customersCampaignSearch}
                              onChange={(e) =>
                                setCustomersCampaignSearch(e.target.value)
                              }
                            />
                            {customersCampaignSearch && (
                              <InputRightElement>
                                <IconButton
                                  icon={<FaTimes />}
                                  size="xs"
                                  variant="ghost"
                                  aria-label="Clear search"
                                  onClick={() =>
                                    setCustomersCampaignSearch("")
                                  }
                                />
                              </InputRightElement>
                            )}
                          </InputGroup>
                          {customersCampaignSearch &&
                            campaignSearchMatches.length > 0 && (
                              <Box
                                mt={2}
                                bg="white"
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                boxShadow="sm"
                                maxH="280px"
                                overflowY="auto"
                              >
                                {campaignSearchMatches.map((c) => {
                                  const org = organizations.find(
                                    (o) => o.id === c.organizationId,
                                  );
                                  return (
                                    <Box
                                      key={c.id}
                                      px={3}
                                      py={2}
                                      cursor="pointer"
                                      _hover={{ bg: "gray.50" }}
                                      borderBottom="1px solid"
                                      borderColor="gray.100"
                                      onClick={() =>
                                        selectCustomersCampaign(c)
                                      }
                                    >
                                      <Text fontSize="sm" fontWeight="medium">
                                        {displayName(c.name)}
                                      </Text>
                                      {org && (
                                        <Text fontSize="xs" color="gray.500">
                                          {displayName(org.name)}
                                        </Text>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          {customersCampaignSearch &&
                            campaignSearchMatches.length === 0 && (
                              <Text fontSize="sm" color="gray.500" mt={2}>
                                No matching campaigns.
                              </Text>
                            )}
                        </Box>
                      ) : (
                        <HStack
                          justify="space-between"
                          align="center"
                          bg="gray.50"
                          borderRadius="md"
                          px={3}
                          py={2}
                        >
                          <HStack spacing={2} minW={0}>
                            <Text fontWeight="semibold">
                              {displayName(selectedCustomersCampaign.name)}
                            </Text>
                            <IdCopyIcon
                              id={selectedCustomersCampaign.id}
                              label="Campaign ID"
                            />
                            {!customersLoading && (
                              <Badge variant="subtle">
                                {customersList.length} customer
                                {customersList.length === 1 ? "" : "s"}
                              </Badge>
                            )}
                          </HStack>
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={exportCustomersCsv}
                              isDisabled={
                                customersLoading || customersList.length === 0
                              }
                            >
                              Export CSV
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={clearCustomersCampaign}
                            >
                              Change campaign
                            </Button>
                          </HStack>
                        </HStack>
                      )}

                      {selectedCustomersCampaign && (
                        <Box overflowX="auto">
                          {customersLoading ? (
                            <HStack py={6} justify="center">
                              <Spinner size="sm" />
                              <Text fontSize="sm" color="gray.500">
                                Loading customers…
                              </Text>
                            </HStack>
                          ) : customersList.length === 0 ? (
                            <Text color="gray.500" py={4}>
                              No customers for this campaign.
                            </Text>
                          ) : (
                            <Table size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Name</Th>
                                  <Th textAlign="center">Zip/Postal</Th>
                                  <Th textAlign="center">Redemptions</Th>
                                  <Th textAlign="center">Created</Th>
                                  <Th textAlign="center">Email</Th>
                                  <Th textAlign="center">ID</Th>
                                  <Th />
                                </Tr>
                              </Thead>
                              <Tbody>
                                {customersList.map((c) => (
                                  <Tr key={c.id}>
                                    <Td>{c.name || "—"}</Td>
                                    <Td textAlign="center">
                                      {c.postalCode || "—"}
                                    </Td>
                                    <Td textAlign="center">
                                      {c.redemptionCount}
                                    </Td>
                                    <Td textAlign="center">
                                      {formatShortDate(c.createdAt) || "—"}
                                    </Td>
                                    <Td textAlign="center">
                                      <EmailIcon email={c.email} />
                                    </Td>
                                    <Td textAlign="center">
                                      <IdCopyIcon
                                        id={c.id}
                                        label="Customer ID"
                                      />
                                    </Td>
                                    <Td>
                                      <Tooltip
                                        label="Open customer page"
                                        hasArrow
                                      >
                                        <IconButton
                                          as={Link}
                                          href={`/customer/${c.id}`}
                                          isExternal
                                          aria-label="Open customer page"
                                          icon={<FaExternalLinkAlt />}
                                          size="xs"
                                          variant="ghost"
                                        />
                                      </Tooltip>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          )}
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0}>
                    <SurveyResultsAdmin
                      campaigns={campaigns}
                      organizations={organizations}
                    />
                  </TabPanel>

                  <TabPanel px={0}>
                    <DigitalCardsAdmin />
                  </TabPanel>

                  <TabPanel px={0}>
                    <TemplatesAdmin />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </VStack>
      </Box>

      <Modal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        isCentered
        closeOnOverlayClick={!deleting}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="red.600">
            Delete {deleteType === "campaign" ? "campaign" : "organization"}
          </ModalHeader>
          <ModalBody>
            <Text mb={3}>
              You are about to permanently delete{" "}
              <strong>{displayName(deleteTarget?.name)}</strong> (
              <Text as="span" fontFamily="mono" fontSize="sm">
                {deleteTarget?.id}
              </Text>
              ).
            </Text>
            <Text fontSize="sm" color="gray.700" mb={3}>
              {deleteType === "campaign"
                ? "This removes the campaign document and its agents subcollection. The parent organization's campaigns array, businesses, sales, and redemption records are NOT cleaned up and will be left as orphaned references. This cannot be undone."
                : "This removes the organization document and its agents subcollection. Related campaigns, businesses, and user memberships are NOT deleted and will be left as orphaned references. This cannot be undone."}
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">
                Type <strong>delete</strong> to confirm:
              </FormLabel>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="delete"
                autoFocus
                isDisabled={deleting}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={closeDeleteModal}
              isDisabled={deleting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={confirmDelete}
              isLoading={deleting}
              isDisabled={deleteConfirmText !== "delete"}
            >
              Delete {deleteType === "campaign" ? "campaign" : "organization"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpen} onClose={impersonating ? () => {} : onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Impersonate user</ModalHeader>
          <ModalBody>
            <Text mb={2}>
              You are about to impersonate{" "}
              <strong>
                {impersonationTarget
                  ? impersonationTarget.email ||
                    impersonationTarget.uid
                  : ""}
              </strong>
              .
            </Text>
            <Text fontSize="sm" color="gray.600">
              Your admin session will be replaced. To return to your own
              account, sign back in with your own credentials.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              isDisabled={impersonating}
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={confirmImpersonate}
              isLoading={impersonating}
            >
              Impersonate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PlatformDashboard;
